import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { profilePath, programPath, programsPath } from "@travily/routes";
import type ProgramsShow from "@travily/types/pages/ProgramsShow";
import { HeartToggleButton } from "@ui/components/HeartToggleButton";
import { Icon } from "@ui/components/Icon";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { Button } from "@ui/primitives/Button";
import { useFavorite } from "@/hooks/use-favorite";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import { messageThreadHref } from "@/lib/routes";
import { ShowView } from "./show.view";

const ProgramShowPage = ({ program, celebrations }: ProgramsShow) => {
  const { share, shared } = useShare();
  const onBack = useNavigateBack(programsPath());
  const favorite = useFavorite({
    favorited: program.favorited,
    favorableType: "Program",
    favorableId: program.id,
  });

  const handle = program.account.handle ?? "";
  const messageHref = messageThreadHref(handle);
  const profileHref = profilePath(handle);

  const handleShare = () => {
    share({
      title: program.name,
      text: t("web.programs.show.share_text", { name: program.name }),
      url: `${location.origin}${programPath(program.id)}`,
    });
  };

  return (
    <PageFrame
      stickyTopBar={false}
      mobileTopBar={
        <TopBarMobile
          onBack={onBack}
          title={program.name}
          actions={
            <>
              <HeartToggleButton
                aria-label={t("common.actions.favorite")}
                isSelected={favorite.isFavorited}
                onChange={favorite.toggle}
                size="24"
              />
              <Button aria-label={t("common.actions.share")} onPress={handleShare}>
                <Icon name="share" size="20" type="sticker" />
              </Button>
            </>
          }
        />
      }
    >
      <Head title={t("web.programs.show.meta_title", { name: program.name })}>
        <meta name="description" content={t("web.programs.show.meta_description", { name: program.name })} />
      </Head>
      <ShowView
        celebrations={celebrations}
        favorited={favorite.isFavorited}
        messageHref={messageHref}
        onFavoriteToggle={favorite.toggle}
        onShare={handleShare}
        profileHref={profileHref}
        program={program}
        shared={shared}
      />
    </PageFrame>
  );
};

export default ProgramShowPage;
